import { useEffect, useState } from 'react';

/**
 * Module-level cache: blobName/cacheKey → boolean
 * Persists across component mounts/unmounts within a session.
 * Using blob name (not signed URL) as key since URLs change across sessions.
 */
const transparencyCache = new Map();

/**
 * Maximum canvas dimension for detection.
 * Images are downscaled to this size before scanning.
 * 256×256 = 65,536 pixels → ~256KB memory, sub-1ms scan time.
 */
const MAX_CANVAS_DIM = 256;

/**
 * Detect whether an image has any transparent pixels by drawing it
 * to an offscreen canvas and scanning the alpha channel.
 *
 * @param {string} imageUrl - The image URL to test (e.g., GCS signed URL)
 * @returns {Promise<boolean>} - true if image has any pixel with alpha < 255
 */
function detectTransparency(imageUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // Determine canvas dimensions (downscale large images)
        const scale = Math.min(1, MAX_CANVAS_DIM / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);

        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data; // Uint8ClampedArray: [R,G,B,A, R,G,B,A, ...]

        let hasAlpha = false;
        // Check every 4th byte (alpha channel) across all pixels
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            hasAlpha = true;
            break;
          }
        }

        // Release canvas memory
        canvas.width = 0;
        canvas.height = 0;

        resolve(hasAlpha);
      } catch (err) {
        // Canvas tainted (CORS), or other error — default to no transparency
        console.warn('[useTransparencyDetection] Detection failed:', err.message);
        resolve(false);
      }
    };

    img.onerror = () => {
      // Image failed to load for detection — default to no transparency
      resolve(false);
    };

    img.src = imageUrl;
  });
}

/**
 * React hook that detects whether an image has transparent pixels.
 *
 * @param {string} imageUrl - The image URL to test
 * @param {string} cacheKey - Stable cache key (blob name preferred over signed URL)
 * @returns {{ hasTransparency: boolean }}
 */
export function useTransparencyDetection(imageUrl, cacheKey) {
  const [hasTransparency, setHasTransparency] = useState(() => {
    // Check cache synchronously on mount for instant result
    const key = cacheKey || imageUrl;
    if (key && transparencyCache.has(key)) {
      return transparencyCache.get(key);
    }
    return false;
  });

  useEffect(() => {
    if (!imageUrl) return;

    const key = cacheKey || imageUrl;

    // If already cached, use cached value
    if (transparencyCache.has(key)) {
      setHasTransparency(transparencyCache.get(key));
      return;
    }

    let cancelled = false;

    detectTransparency(imageUrl).then((result) => {
      if (!cancelled) {
        transparencyCache.set(key, result);
        setHasTransparency(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [imageUrl, cacheKey]);

  return { hasTransparency };
}

export default useTransparencyDetection;
